import { useContext, useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { PageContext } from "../../contexts/PageContext";
import { Deployment } from "../../models/Deployment";
import { AddLocationAlt, Delete } from "@mui/icons-material";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { deleteRecord, loadOrgRecords } from "../../api/connection";
import { UserRole } from "../../models/User";
import Minibar from "../../components/Minibar";
import PageHeader from "../../components/PageHeader";
import { getCollectionName } from "../../models/ModelNavigator";
import { createSiteAnchor, SiteAnchor } from "../../models/SiteAnchor";

export function DeploymentsPage() {
  const { isLoggedIn, user } = useContext(PageContext);
  const navigate = useNavigate();
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [mapIdToName, setMapIdToName] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Deployment | null;
    direction: "asc" | "desc";
  }>({
    key: null,
    direction: "asc",
  });
  const [filter, setFilter] = useState<string>("");
  const [filteredRows, setFilteredRows] = useState<Deployment[]>([]);

  const fetchDeployments = async (role: UserRole) => {
    try {
      const res = await loadOrgRecords<Deployment>("deployments");
      const sortedDeployments = res.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      // const map = ;
      const sites = await loadOrgRecords<SiteAnchor>(
        getCollectionName(createSiteAnchor())
      );
      for (const site of sites) {
        if (!site.id) {
          site.id = "No Name";
        }
      }
      const mapIdToName: Record<string, string> = {};
      sites.forEach((site) => {
        mapIdToName[site._id] = site.id;
      });
      setDeployments(sortedDeployments);
      setMapIdToName(mapIdToName);
    } catch (err) {
      console.error("Failed to fetch deployments:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // if (!isLoggedIn || (user && user?.role !== UserRole.SystemAdmin)) {
    //   navigate("/maintenance/404");
    // } else if (isLoggedIn && user?.role === UserRole.SystemAdmin) {
    //   fetchDeployments(user.role);
    // }
    if (isLoggedIn && user) {
      fetchDeployments(user.role);
    } else {
      navigate("/maintenance/404");
    }
  }, [isLoggedIn, user]);

  const handleSort = (key: keyof Deployment) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedDeployments = [...deployments].sort((a, b) => {
      if (key === "siteId") {
        const aMapName = mapIdToName[a.siteId] || "";
        const bMapName = mapIdToName[b.siteId] || "";
        if (aMapName < bMapName) return direction === "asc" ? -1 : 1;
        if (aMapName > bMapName) return direction === "asc" ? 1 : -1;
        return 0;
      }

      const aValue = a[key] ?? "";
      const bValue = b[key] ?? "";

      if (aValue < bValue) return direction === "asc" ? -1 : 1;
      if (aValue > bValue) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setDeployments(sortedDeployments);
  };

  useEffect(() => {
    const filtered = deployments.filter(
      (deployment) =>
        deployment.name.toLowerCase().includes(filter.toLowerCase()) ||
        (deployment.siteId &&
          mapIdToName[deployment.siteId]
            ?.toLowerCase()
            .includes(filter.toLowerCase()))
    );
    setFilteredRows(filtered);
  }, [filter, deployments]);

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            m: 0,
            backgroundColor: "#212121",
            color: "#ECECEC",
            minHeight: "100vh",
          }}
        >
          <Backdrop
            open={loading}
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          <PageHeader
            title={"Deployments"}
            rightComponent={[
              <Minibar
                minibarItems={[
                  {
                    text: "Add Deployment",
                    icon: <AddLocationAlt />,
                    nav: `/edit-deployment/new`, // Adjust path to match your routes
                  },
                ]}
                barLength={1}
                searchLabel="Search Deployments"
                onSearch={(e) => setFilter(e)}
              />,
            ]}
          />
          <TableContainer>
            <Table aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell align="left" width="20%">
                    <TableSortLabel
                      active={sortConfig.key === "name"}
                      direction={
                        sortConfig.key === "name" ? sortConfig.direction : "asc"
                      }
                      onClick={() => handleSort("name")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      Deployment Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left" width="40%">
                    Sim Scene Name
                  </TableCell>
                  <TableCell align="left" width="40%" colSpan={2}>
                    Link to Deployment
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((deployment) => (
                  <TableRow hover key={deployment._id}>
                    <TableCell align="left">
                      <Link
                        onClick={() =>
                          navigate(`/edit-deployment/${deployment._id}`)
                        }
                        style={{ color: "#b0d1ff", cursor: "pointer" }}
                      >
                        {deployment.name}
                      </Link>
                    </TableCell>
                    <TableCell align="left">
                      {mapIdToName[deployment.siteId] || "No Scene"}
                    </TableCell>
                    <TableCell align="left">
                      <Link
                        to={`/deployment/${deployment._id}`}
                        component={RouterLink}
                        sx={{ color: "#b0d1ff", cursor: "pointer" }}
                      >
                        Go to Deployment
                      </Link>
                    </TableCell>
                    <TableCell align="left">
                      <IconButton
                        onClick={async () => {
                          await deleteRecord("deployments", deployment);
                          setDeployments(
                            deployments.filter((d) => d._id !== deployment._id)
                          );
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </>
  );
}
